/* eslint-disable @angular-eslint/directive-selector */
import { isPlatformBrowser } from '@angular/common';
import type { OnDestroy } from '@angular/core';
import { Directive, ElementRef, Injector, PLATFORM_ID, Renderer2, effect, inject, input } from '@angular/core';
import { LoginFeature } from '@hyundai/ng-common-lib';
import { type Identifiers } from '@hyundai/shared-lib/edit';
import { Store } from '@ngrx/store';
import { DialogService } from '@pixelgenau/ngx-dialog';
import { type EditModeProvider } from '../provider/edit-mode';

const PENCIL_CONTENT_CLASS = 'edit-pencil-content';

export type PencilType = 'text' | 'image' | 'theme';

@Directive({
    selector: '[editable]',
    host: {
        class: 'flex items-center gap-10 relative',
    },
    standalone: true,
})
export class EditableDirective implements OnDestroy {
    editable = input.required<Identifiers>();

    type = input<PencilType>('text');
    disabled = input(false);

    protected dialogService = inject(DialogService);

    protected store = inject(Store);
    protected isLoggedIn = this.store.selectSignal(LoginFeature.state.selectIsLoggedIn);

    protected renderer = inject(Renderer2);
    protected elementRef = inject(ElementRef<HTMLElement>);
    protected pencilElement: HTMLElement | undefined;
    protected isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

    protected injector = inject(Injector);
    protected editmodeProvider?: EditModeProvider;

    enableEditMode = effect(() => {
        if (!this.isBrowser) {
            return;
        }
        const isLoggedIn = this.isLoggedIn();
        this.clearExistingPencilElements();
        if (!isLoggedIn || this.editmodeProvider) {
            return;
        }
        this.initEditMode();
    });

    protected async initEditMode() {
        const E = (await import('../provider/edit-mode')).EditModeProvider;
        this.editmodeProvider = await E.getInstance(this.injector, this.store, this.dialogService);
        if (!this.pencilElement) {
            this.pencilElement = this._createPencilElement();
        }
    }

    ngOnDestroy(): void {
        if (this.isBrowser) {
            removeEventListener('click', this.edit.bind(this));
        }
    }

    protected clearExistingPencilElements() {
        const pencils = this.elementRef.nativeElement.querySelectorAll(`:scope > .${PENCIL_CONTENT_CLASS}`);
        for (const pencilElement of Array.from(pencils)) {
            (pencilElement as HTMLElement).remove();
        }
    }

    protected _createPencilElement(): HTMLElement {
        const pencilElement = this.renderer.createElement('i') as HTMLElement;
        const activeClass = 'edit-pencil-active';
        this.elementRef.nativeElement.appendChild(pencilElement);
        pencilElement.classList.add(
            PENCIL_CONTENT_CLASS,
            activeClass,
            'cursor-pointer',
            'icon-fontawesome-edit',
            'hover:opacity-70',
            'transition-opacity',
        );
        if (this.type().includes('image')) {
            pencilElement.classList.add(
                'absolute',
                'top-5',
                'right-5',
                'text-16',
                'bg-blue',
                'text-white',
                'px-6',
                'py-4',
            );
        }
        if (this.type().includes('theme')) {
            pencilElement.innerHTML = 'Farbschema ändern';
            pencilElement.classList.add(
                'absolute',
                'top-5',
                'right-5',
                'text-16',
                'bg-blue',
                'text-white',
                'px-6',
                'py-4',
            );
        } else {
            pencilElement.classList.add('text-blue', 'text-16');
        }
        pencilElement.addEventListener('click', this.edit.bind(this), { passive: true });
        return pencilElement;
    }

    async edit() {
        await this.editmodeProvider?.openEditDialog(this.editable());
    }
}
